import { useState } from 'react';
import { Copy, Check, Share2, Gift } from 'lucide-react';
import { Button, Card, CardContent } from '@jeraisonne/ui';
import { QrCodeImage } from '@/components/qr-code';
import { useCreatePurchaseGrant } from '@/lib/student/purchase-grants';

/**
 * Carte « Offrir cet accès à mon enfant » (handoff acheteur → enfant).
 * Génère un lien/QR à usage unique ; l'enfant le scanne pour récupérer l'accès
 * sur son propre compte (1 achat = 1 enfant).
 */
export function OfferToChildCard() {
  const createGrant = useCreatePurchaseGrant();
  const [url, setUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // l'utilisateur peut copier manuellement
    }
  }

  async function handleShare() {
    if (!url) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Ton accès Jeraisonne',
          text: 'Scanne ou ouvre ce lien pour activer ton accès :',
          url,
        });
      } catch {
        // annulé
      }
    } else {
      handleCopy();
    }
  }

  return (
    <Card className="border-primary-200 bg-primary-50/40">
      <CardContent className="space-y-4 py-4">
        <div className="flex items-start gap-3">
          <span className="bg-primary-100 text-primary-600 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
            <Gift className="h-5 w-5" />
          </span>
          <div>
            <p className="text-ink-900 text-sm font-semibold">Offrir cet accès à mon enfant</p>
            <p className="text-ink-500 mt-0.5 text-xs">
              Génère un lien à usage unique : ton enfant le scanne pour activer l&apos;accès sur son
              compte.
            </p>
          </div>
        </div>

        {!url ? (
          <Button
            type="button"
            fullWidth
            onClick={() =>
              createGrant.mutate(undefined, {
                onSuccess: (g) => setUrl(g.url),
              })
            }
            disabled={createGrant.isPending}
          >
            {createGrant.isPending ? 'Génération…' : 'Générer le lien pour mon enfant'}
          </Button>
        ) : (
          <div className="flex items-center gap-4">
            <div className="border-ink-100 rounded-xl border bg-white p-2">
              <QrCodeImage value={url} size={120} />
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <p className="text-ink-500 text-xs">Lien valable 48 h, utilisable une seule fois.</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  className="gap-1.5"
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? 'Copié' : 'Copier le lien'}
                </Button>
                <Button type="button" size="sm" onClick={handleShare} className="gap-1.5">
                  <Share2 className="h-3.5 w-3.5" />
                  Partager
                </Button>
              </div>
            </div>
          </div>
        )}

        {createGrant.isError && (
          <p className="text-accent-600 text-xs">{(createGrant.error as Error).message}</p>
        )}
      </CardContent>
    </Card>
  );
}
